
import Vue, { PropType } from 'vue';
import _sortBy from 'lodash/sortBy';

interface SliderSelectItem {
	label: string | number
	value: number
}

export default Vue.extend({
	name: 'SliderSelect',
	props: {
		value: { type: Number, required: true },
		items: { type: Array as PropType<SliderSelectItem[]>, required: true },
		dampingFactor: { type: Number, default: 0.6 }, // 0 = pure gap-based, 1 = completely equal
		disabled: { type: Boolean, default: false },
	},
	data () {
		return {
			isDragging: false,
		};
	},
	computed: {
		orderedItems () {
			// Check if all labels are numbers (either number type or numeric strings)
			const allLabelsAreNumeric = this.items.every(item => {
				return typeof item.label === 'number' ||
					(typeof item.label === 'string' && !isNaN(Number(item.label)));
			});

			if (allLabelsAreNumeric) {
				// Sort by label value (convert strings to numbers for proper sorting)
				return _sortBy(this.items, item => {
					return typeof item.label === 'number' ? item.label : Number(item.label);
				});
			} else {
				// Sort by value if labels are not all numeric
				return _sortBy(this.items, 'value');
			}
		},
		valueToPositionMap () {
			const map: Record<number, number> = {};

			const positions = this.dynamicSpacingPositions;
			this.orderedItems.forEach((item, index) => {
				map[item.value] = positions[index] || 0;
			});

			return map;
		},
		getCurrentItem () {
			return this.orderedItems.find(item => item.value === this.value) || this.orderedItems[0];
		},
		dynamicSpacingPositions () {
			if (this.orderedItems.length <= 1) return [0];

			// Use numeric labels if available, otherwise use values
			const calculationValues = this.orderedItems.map(item => {
				const labelNum = typeof item.label === 'number' ? item.label : Number(item.label);
				return !isNaN(labelNum) ? labelNum : item.value;
			});

			// Calculate gaps between adjacent calculation values
			const gaps: number[] = [];
			for (let i = 1; i < calculationValues.length; i++) {
				gaps.push(calculationValues[i] - calculationValues[i - 1]);
			}

			// Calculate total gap
			const totalGap = gaps.reduce((sum, gap) => sum + gap, 0);

			// Linear spacing percentage
			const linearPercent = 100 / gaps.length;

			// Damping factor: how much of the linear spacing to keep as minimum
			const minSpacePercent = (this.dampingFactor * linearPercent);

			// Calculate actual gap proportions
			const gapProportions = gaps.map(gap => gap / totalGap);

			// Calculate remaining space to distribute
			const totalMinSpace = minSpacePercent * gaps.length;
			const remainingSpace = 100 - totalMinSpace;

			// Calculate final space percentages
			const spacePercents: number[] = [];
			for (let i = 0; i < gaps.length; i++) {
				const additionalSpace = remainingSpace * gapProportions[i];
				const finalSpace = minSpacePercent + additionalSpace;
				spacePercents.push(finalSpace);
			}

			// Calculate positions based on space percentages
			const positions: number[] = [0]; // First item at 0%
			let currentPosition = 0;

			for (let i = 0; i < spacePercents.length; i++) {
				currentPosition += spacePercents[i];
				positions.push(currentPosition);
			}

			// Ensure last position is exactly 100%
			positions[positions.length - 1] = 100;

			return positions;
		}
	},
	methods: {
		handleTrackClick (event: MouseEvent) {
			if (this.disabled) return;

			const track = this.$refs.rangeTrack as HTMLElement;
			const rect = track.getBoundingClientRect();
			const clickX = event.clientX - rect.left;
			const percentage = (clickX / rect.width) * 100;

			// Find closest value by position
			let closestValue = this.orderedItems[0].value;
			let closestDistance = Math.abs(percentage - this.getPositionForValue(closestValue));

			for (const item of this.orderedItems) {
				const distance = Math.abs(percentage - this.getPositionForValue(item.value));
				if (distance < closestDistance) {
					closestDistance = distance;
					closestValue = item.value;
				}
			}

			this.$emit('input', closestValue);
		},
		handleTickClick (value: number) {
			if (this.disabled) return;
			this.$emit('input', value);
		},
		startDrag (event: MouseEvent) {
			if (this.disabled) return;

			event.preventDefault();
			this.isDragging = true;
			const track = this.$refs.rangeTrack as HTMLElement;

			const handleMouseMove = (e: MouseEvent) => {
				const rect = track.getBoundingClientRect();
				const moveX = e.clientX - rect.left;
				const percentage = Math.max(0, Math.min(100, (moveX / rect.width) * 100));

				// Find closest value by position
				let closestValue = this.orderedItems[0].value;
				let closestDistance = Math.abs(percentage - this.getPositionForValue(closestValue));

				for (const item of this.orderedItems) {
					const distance = Math.abs(percentage - this.getPositionForValue(item.value));
					if (distance < closestDistance) {
						closestDistance = distance;
						closestValue = item.value;
					}
				}

				this.$emit('input', closestValue);
			};

			const handleMouseUp = () => {
				this.isDragging = false;
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
			};

			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
		},
		getPositionForValue (value: number) {
			return this.valueToPositionMap[value] || 0;
		},
	},
});
